// Global Instructions Rule Applied!
//
// Configuration → Users & Roles (admin only; docs/USER_RIGHTS_DESIGN.md
// section 7). Three sections over function_user_rights_admin.sql:
//
//   Roles   - create, rename, describe, reorder and remove roles; add or take
//             off members. Admin and Everyone are built in: fixed, and
//             Everyone has no members (it admits every user).
//   Users   - the people who may sign in, and anyone else holding a role, with
//             the roles each holds. Someone not listed is added from a role's
//             Members picker.
//   Rights  - the matrix: one row per capability, one switch per role, editing
//             the capability's DEFAULT rule. Everyone and named roles exclude
//             each other, as in the Pipelines panel's "Who can move"
//             (moveRoleSelection.ts): with Everyone on, the role switches show
//             on and are disabled; switching Everyone off leaves admins only
//             until roles are switched on. A capability with per-record rules
//             links "N overrides" to the screen where they are edited
//             (cfg.capabilities.overrides_menu_key).
//
// The database decides every rule: a role a right still names cannot be
// removed, Admin keeps a member and an administrator cannot take themself off
// it. The panel disables what it can see will be refused, with the reason, and
// shows the database's sentence for the rest.
//
// Chrome copied from StatementTypesPanel.tsx (the newest compliant sibling):
// FormSection + antd Table inside it, per-field autosave, the inline refusal
// Alert and a reload after every write; the New Role modal and the row
// delete follow FundStatusesPanel / WaterfallTemplatesPanel; the matrix cells
// are GridSwitchCell. A change of rights reaches other users on their next
// /global-configs load; the database enforces it at once.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Input, Modal, Popconfirm, Select, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import GridSwitchCell from '../../../core/components/GridSwitchCell.tsx'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import InlineTextField from './InlineTextField.tsx'
import InlineNumberField from './InlineNumberField.tsx'
import type {
  UserRightsCapability,
  UserRightsRole,
  UserRightsUser,
  UserRightsUserOption
} from '../../../state_manager/data_models/user_rights_admin_model.ts'
import {
  createRole,
  deleteRole,
  fetchUserRightsCapabilities,
  fetchUserRightsRoles,
  fetchUserRightsUserOptions,
  fetchUserRightsUsers,
  saveCapabilityDefault,
  setRoleMember,
  updateRole,
  type RoleFields,
  type ServiceResult
} from '../services/userRightsConfigService.ts'
import { CONFIG_MODULES_BY_KEY, CONFIG_PANEL_SCROLL_CLASS } from '../configModules.ts'

const { Text } = Typography

const RULES_HELP =
  'How many rights name this role: the switches under Rights, and records with a rule of their own, such as ' +
  "a pipeline stage's Who can move. A role that is still named cannot be removed."
const OVERRIDES_HELP = 'Records with a rule of their own. Their rule wins over the switches on this row.'

interface PanelData {
  roles: UserRightsRole[]
  users: UserRightsUser[]
  userOptions: UserRightsUserOption[]
  capabilities: UserRightsCapability[]
}

const EMPTY: PanelData = { roles: [], users: [], userOptions: [], capabilities: [] }

/** A service result's data, or a throw carrying its sentence. */
function unwrap<T>(result: ServiceResult<T>): T {
  if (result.error !== null || result.data === null) throw new Error(result.error ?? 'No data returned')
  return result.data
}

/** The default rule's roles after one matrix switch. Everyone and named roles
 *  exclude each other: Everyone on replaces the roles, off leaves admins only. */
export function nextDefaultRoleIds(
  current: string[],
  roleId: string,
  on: boolean,
  everyoneRoleId: string | null
): string[] {
  if (roleId === everyoneRoleId) return on ? [roleId] : []
  const named = current.filter((id) => id !== everyoneRoleId && id !== roleId)
  return on ? [...named, roleId] : named
}

export default function UsersRolesPanel(): React.ReactElement {
  const navigate = useNavigate()
  const token = (): string | null => getAccessToken(store.getState().auth)

  const [data, setData] = useState<PanelData>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [refusal, setRefusal] = useState<string | null>(null)
  // Bumped on every load and part of the inline fields' keys, so a refused
  // edit snaps back to the stored value even when the value did not change.
  const [loadVersion, setLoadVersion] = useState(0)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [roles, users, userOptions, capabilities] = await Promise.all([
        fetchUserRightsRoles(token()),
        fetchUserRightsUsers(token()),
        fetchUserRightsUserOptions(token()),
        fetchUserRightsCapabilities(token())
      ])
      setData({
        roles: unwrap(roles),
        users: unwrap(users),
        userOptions: unwrap(userOptions),
        capabilities: unwrap(capabilities)
      })
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Failed to load users and roles'
      setRefusal(text)
      showErrorToast(text)
    } finally {
      setLoadVersion((n) => n + 1)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const activateRef = useRefreshOnActivate(load)

  /** Every write goes through here: clear the last refusal, act, reload. On
   *  failure keep the message visible AND reload, so a control the server
   *  refused snaps back to the stored value. */
  const run = async <T,>(
    key: string,
    action: () => Promise<ServiceResult<T>>,
    success: (saved: T) => string
  ): Promise<boolean> => {
    setRefusal(null)
    setBusyKey(key)
    try {
      const saved = unwrap(await action())
      toast.success(success(saved))
      await load()
      return true
    } catch (e) {
      const text = e instanceof Error ? e.message : 'The change was not saved'
      setRefusal(text)
      showErrorToast(text)
      await load()
      return false
    } finally {
      setBusyKey(null)
    }
  }

  const everyoneRole = useMemo(() => data.roles.find((r) => r.roles__is_everyone) ?? null, [data.roles])
  const everyoneRoleId = everyoneRole?.roles__id ?? null
  const superuserRoles = useMemo(() => data.roles.filter((r) => r.roles__is_superuser), [data.roles])
  const namedRoles = useMemo(
    () => data.roles.filter((r) => !r.roles__is_superuser && !r.roles__is_everyone),
    [data.roles]
  )
  // Users hold Admin and the named roles; Everyone is implicit.
  const holdableRoleOptions = useMemo(
    () => data.roles.filter((r) => !r.roles__is_everyone).map((r) => ({ value: r.roles__id, label: r.roles__name })),
    [data.roles]
  )
  const userOptions = useMemo(
    () => data.userOptions.map((u) => ({ value: u.value, label: u.label, title: u.system_users__email })),
    [data.userOptions]
  )
  const roleName = (roleId: string): string => data.roles.find((r) => r.roles__id === roleId)?.roles__name ?? 'the role'

  // ── Roles ────────────────────────────────────────────────────────────────

  const saveRole = (row: UserRightsRole, fields: RoleFields): Promise<boolean> =>
    run(`role-${row.roles__id}`, () => updateRole(row.roles__id, fields, token()), (saved) =>
      fields.name !== undefined ? `Renamed to ${saved.roles__name}.` : `${saved.roles__name} saved.`
    )

  const changeMember = (roleId: string, systemUserId: string, isMember: boolean): Promise<boolean> =>
    run(
      `member-${roleId}-${systemUserId}`,
      () => setRoleMember(roleId, systemUserId, isMember, token()),
      (saved) => saved.prog__summary
    )

  const submitNewRole = async (): Promise<void> => {
    const name = newName.trim()
    if (!name) return
    const saved = await run(
      'new-role',
      () => createRole({ name, description: newDescription.trim() }, token()),
      (role) => `Role ${role.roles__name} added. Add its members, then switch on its rights below.`
    )
    if (saved) {
      setNewOpen(false)
      setNewName('')
      setNewDescription('')
    }
  }

  const removeReason = (row: UserRightsRole): string | null => {
    if (row.prog__is_built_in) return `${row.roles__name} is built in and cannot be removed.`
    if (row.prog__rule_count > 0)
      return `Take ${row.roles__name} off its rights first: ${row.prog__rule_count} ${
        row.prog__rule_count === 1 ? 'right names' : 'rights name'
      } it.`
    return null
  }

  const roleColumns: ColumnsType<UserRightsRole> = [
    {
      title: 'Role',
      dataIndex: 'roles__name',
      width: 200,
      render: (v: string, row) =>
        row.prog__is_built_in ? (
          <span className='flex items-center gap-2'>
            <Text>{v}</Text>
            <Tag color='blue'>Built in</Tag>
          </span>
        ) : (
          <InlineTextField
            key={`${row.roles__id}-name-${loadVersion}`}
            value={v}
            maxLength={60}
            onCommit={(next) => saveRole(row, { name: next })}
          />
        )
    },
    {
      title: 'Description',
      dataIndex: 'roles__description',
      render: (v: string | null | undefined, row) =>
        row.prog__is_built_in ? (
          <Text type='secondary'>{v ?? ''}</Text>
        ) : (
          <InlineTextField
            key={`${row.roles__id}-description-${loadVersion}`}
            value={v ?? ''}
            placeholder='What the role is for'
            onCommit={(next) => saveRole(row, { description: next })}
          />
        )
    },
    {
      title: 'Members',
      dataIndex: 'prog__member_ids',
      width: 380,
      render: (memberIds: string[], row) =>
        row.roles__is_everyone ? (
          <Text type='secondary'>Every user who signs in</Text>
        ) : (
          <Select
            size='small'
            mode='multiple'
            showSearch
            optionFilterProp='label'
            maxTagCount='responsive'
            style={{ width: '100%' }}
            placeholder='No members'
            aria-label={`Members of ${row.roles__name}`}
            loading={busyKey?.startsWith(`member-${row.roles__id}-`) ?? false}
            options={userOptions}
            value={memberIds}
            onSelect={(userId: string) => void changeMember(row.roles__id, userId, true)}
            onDeselect={(userId: string) => void changeMember(row.roles__id, userId, false)}
          />
        )
    },
    {
      title: (
        <span>
          Rights
          <HelpTip title={RULES_HELP} />
        </span>
      ),
      dataIndex: 'prog__rule_count',
      width: 90,
      align: 'center',
      render: (v: number, row) => (row.roles__is_superuser ? <Text type='secondary'>All</Text> : v)
    },
    {
      title: 'Order',
      dataIndex: 'roles__sort_order',
      width: 100,
      render: (v: number, row) =>
        row.prog__is_built_in ? (
          <Text type='secondary'>{v}</Text>
        ) : (
          <InlineNumberField
            key={`${row.roles__id}-order-${loadVersion}`}
            value={v}
            min={0}
            max={10000}
            onCommit={(next) =>
              next === null ? undefined : saveRole(row, { sort_order: next })
            }
          />
        )
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      align: 'center',
      render: (_: unknown, row) => {
        const reason = removeReason(row)
        const button = (
          <Button
            type='text'
            size='small'
            danger
            icon={<DeleteOutlined />}
            aria-label={`Remove ${row.roles__name}`}
            disabled={reason !== null}
            loading={busyKey === `remove-${row.roles__id}`}
          />
        )
        if (reason !== null)
          return (
            <Tooltip title={reason}>
              <span>{button}</span>
            </Tooltip>
          )
        return (
          <Popconfirm
            title={`Remove the role ${row.roles__name}?`}
            description={
              row.prog__member_count > 0
                ? `Its ${row.prog__member_count} ${row.prog__member_count === 1 ? 'member loses' : 'members lose'} it.`
                : undefined
            }
            okText='Remove'
            okButtonProps={{ danger: true }}
            onConfirm={() =>
              run(`remove-${row.roles__id}`, () => deleteRole(row.roles__id, token()), (removed) =>
                `Role ${removed.roles__name} removed.`
              )
            }
          >
            {button}
          </Popconfirm>
        )
      }
    }
  ]

  // ── Users ────────────────────────────────────────────────────────────────

  const userColumns: ColumnsType<UserRightsUser> = [
    {
      title: 'User',
      dataIndex: 'prog__name',
      width: 280,
      render: (v: string, row) => (
        <div>
          <Text>{v}</Text>
          {row.system_users__email && (
            <div>
              <Text type='secondary' style={{ fontSize: 12 }}>
                {row.system_users__email}
              </Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Roles',
      dataIndex: 'prog__role_ids',
      render: (roleIds: string[], row) => (
        <Select
          size='small'
          mode='multiple'
          style={{ width: '100%', maxWidth: 520 }}
          placeholder='Everyone only'
          aria-label={`Roles of ${row.prog__name}`}
          loading={busyKey?.endsWith(`-${row.system_users__id}`) ?? false}
          options={holdableRoleOptions}
          value={roleIds}
          onSelect={(roleId: string) => void changeMember(roleId, row.system_users__id, true)}
          onDeselect={(roleId: string) => void changeMember(roleId, row.system_users__id, false)}
        />
      )
    }
  ]

  // ── Rights ───────────────────────────────────────────────────────────────

  const saveDefault = (row: UserRightsCapability, roleId: string, on: boolean): Promise<boolean> =>
    run(
      `default-${row.capabilities__capability_key}`,
      () =>
        saveCapabilityDefault(
          row.capabilities__capability_key,
          nextDefaultRoleIds(row.prog__default_role_ids, roleId, on, everyoneRoleId),
          token()
        ),
      (saved) => saved.prog__summary
    )

  // One "Area" cell per run of rows with the same module.
  const areaRowSpan = (index: number): number => {
    const rows = data.capabilities
    const module = rows[index]?.capabilities__module
    if (index > 0 && rows[index - 1]?.capabilities__module === module) return 0
    let n = 1
    while (rows[index + n]?.capabilities__module === module) n += 1
    return n
  }

  const rightsColumns: ColumnsType<UserRightsCapability> = [
    {
      title: 'Area',
      dataIndex: 'prog__module_label',
      width: 150,
      onCell: (_, index) => ({ rowSpan: areaRowSpan(index ?? 0) })
    },
    {
      title: 'Right',
      dataIndex: 'prog__label',
      render: (v: string, row) => (
        <span>
          {v}
          <HelpTip title={row.capabilities__description ?? undefined} />
        </span>
      )
    },
    ...superuserRoles.map((role) => ({
      title: role.roles__name,
      key: `role-${role.roles__id}`,
      width: 110,
      align: 'center' as const,
      render: (_: unknown, row: UserRightsCapability) => (
        <GridSwitchCell
          checked
          ariaLabel={`${role.roles__name} may ${row.capabilities__name}`}
          onSave={async () => false}
          disabledReason={`${role.roles__name} can do everything.`}
        />
      )
    })),
    ...namedRoles.map((role) => ({
      title: role.roles__name,
      key: `role-${role.roles__id}`,
      width: 110,
      align: 'center' as const,
      render: (_: unknown, row: UserRightsCapability) => {
        const everyoneOn = everyoneRoleId !== null && row.prog__default_role_ids.includes(everyoneRoleId)
        return (
          <GridSwitchCell
            checked={everyoneOn || row.prog__default_role_ids.includes(role.roles__id)}
            ariaLabel={`${role.roles__name} may ${row.capabilities__name}`}
            onSave={(next) => saveDefault(row, role.roles__id, next)}
            disabledReason={
              everyoneOn ? `Everyone may already. Switch Everyone off to choose roles.` : null
            }
          />
        )
      }
    })),
    ...(everyoneRole
      ? [
          {
            title: everyoneRole.roles__name,
            key: `role-${everyoneRole.roles__id}`,
            width: 110,
            align: 'center' as const,
            render: (_: unknown, row: UserRightsCapability) => (
              <GridSwitchCell
                checked={row.prog__default_role_ids.includes(everyoneRole.roles__id)}
                ariaLabel={`Everyone may ${row.capabilities__name}`}
                onSave={(next) => saveDefault(row, everyoneRole.roles__id, next)}
              />
            )
          }
        ]
      : []),
    {
      title: (
        <span>
          Overrides
          <HelpTip title={OVERRIDES_HELP} />
        </span>
      ),
      key: 'overrides',
      width: 150,
      render: (_: unknown, row) => {
        if (row.prog__override_count === 0) return <Text type='secondary'>None</Text>
        const label = `${row.prog__override_count} ${row.prog__override_count === 1 ? 'override' : 'overrides'}`
        const menuKey = row.capabilities__overrides_menu_key
        if (!menuKey) return label
        const where = CONFIG_MODULES_BY_KEY[menuKey]?.title
        return (
          <Tooltip title={where ? `Edited in Configuration → ${where}` : undefined}>
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => navigate(`/${menuKey}`)}
            >
              {label}
            </button>
          </Tooltip>
        )
      }
    }
  ]

  return (
    <div ref={activateRef} className={`${CONFIG_PANEL_SCROLL_CLASS} space-y-4`}>
      {refusal && <Alert type='error' showIcon closable message={refusal} onClose={() => setRefusal(null)} />}

      <FormSection
        title='Roles'
        titleColor='blue'
        scrollable={false}
        loading={loading}
        headerActions={
          <span className='flex items-center gap-2'>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Who holds which role
            </Text>
            <Button type='primary' size='small' icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
              New Role
            </Button>
            <Button size='small' icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </span>
        }
      >
        <Table size='small' rowKey='roles__id' pagination={false} dataSource={data.roles} columns={roleColumns} />
        <Text type='secondary' style={{ fontSize: 12 }}>
          Admin can do everything and manages this screen; it keeps at least one member, and nobody can take
          themself off it. Everyone is every user who signs in. A role that a right still names cannot be removed.
        </Text>
      </FormSection>

      <FormSection title='Users' titleColor='green' scrollable={false} loading={loading}>
        <Table
          size='small'
          rowKey='system_users__id'
          pagination={false}
          dataSource={data.users}
          columns={userColumns}
        />
        <Text type='secondary' style={{ fontSize: 12 }}>
          The people who may sign in, and anyone else who holds a role. To give someone not listed a role, add them
          to the role&apos;s members above.
        </Text>
      </FormSection>

      <FormSection title='Rights' titleColor='purple' scrollable={false} loading={loading}>
        <Table
          size='small'
          rowKey='capabilities__id'
          pagination={false}
          bordered
          dataSource={data.capabilities}
          columns={rightsColumns}
          scroll={{ x: 'max-content' }}
        />
        <Text type='secondary' style={{ fontSize: 12 }}>
          Who may do each thing, unless a record has a rule of its own. Switching Everyone off leaves admins only
          until you switch roles on. A change applies at once; other users see their buttons change the next time
          they load the app.
        </Text>
      </FormSection>

      <Modal
        title='New role'
        open={newOpen}
        okText='Add'
        okButtonProps={{ disabled: !newName.trim() }}
        confirmLoading={busyKey === 'new-role'}
        onOk={() => void submitNewRole()}
        onCancel={() => setNewOpen(false)}
        destroyOnClose
      >
        <div className='flex flex-col gap-3'>
          <div>
            <Text strong>Name</Text>
            <Input
              value={newName}
              maxLength={60}
              placeholder='e.g. Finance'
              aria-label='Role name'
              onChange={(e) => setNewName(e.target.value)}
              onPressEnter={() => void submitNewRole()}
              style={{ marginTop: 4 }}
              autoFocus
            />
          </div>
          <div>
            <Text strong>Description</Text>
            <Input.TextArea
              value={newDescription}
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder='What the role is for'
              aria-label='Role description'
              onChange={(e) => setNewDescription(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
